import { type InputHTMLAttributes, type ReactNode, useId } from 'react';
import styles from './TextInput.module.css';

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
  size?: 'sm' | 'md';
}

export function TextInput({
  label,
  hint,
  error,
  leadingIcon,
  size = 'md',
  className,
  id: idProp,
  ...rest
}: TextInputProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const inputCls = [
    styles.input,
    size === 'sm' ? styles.inputSm : styles.inputMd,
    leadingIcon ? styles.hasLeadingIcon : '',
    error ? styles.inputError : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.inputWrapper}>
        {leadingIcon && (
          <span className={styles.leadingIcon} aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <input
          id={id}
          className={inputCls}
          aria-describedby={errorId ?? hintId}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
      </div>
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}
